import {
  Alert,
  Breadcrumbs,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  Tooltip,
  Typography,
} from "@mui/material";
import { LoadingButton } from "@mui/lab";
import AddIcon from "@mui/icons-material/Add";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import ArticleOutlinedIcon from "@mui/icons-material/ArticleOutlined";
import CategoryOutlinedIcon from "@mui/icons-material/CategoryOutlined";
import React, { useEffect, useMemo, useState } from "react";
import { Form, Modal } from "react-bootstrap";
import { Link } from "react-router-dom";
import { instance } from "../../Const/ApiHeader";
import {
  ALL_BLOGS,
  BLOG_CATEGORIES,
  DELETE_BLOG,
  DELETE_BLOG_CATEGORY,
  resolveImageUrl,
} from "../../Const/ApiConst";

function formatDate(value) {
  if (!value) return "-";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return "-";
  return date.toLocaleDateString("en-IN", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
}

function Blogs() {
  const [posts, setPosts] = useState([]);
  const [categories, setCategories] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [search, setSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState("all");
  const [categoryFilter, setCategoryFilter] = useState("all");
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [deleteLoading, setDeleteLoading] = useState(false);
  const [deleteError, setDeleteError] = useState("");

  const [showCategories, setShowCategories] = useState(false);
  const [categoryName, setCategoryName] = useState("");
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState("");

  const loadPosts = async () => {
    setLoading(true);
    setError("");
    try {
      const params = {};
      if (statusFilter !== "all") params.status = statusFilter;
      if (categoryFilter !== "all") params.category = categoryFilter;
      if (search.trim()) params.search = search.trim();

      const [postsRes, categoriesRes] = await Promise.all([
        instance.get(ALL_BLOGS, { params }),
        instance.get(BLOG_CATEGORIES),
      ]);
      setPosts(postsRes.data || []);
      setCategories(categoriesRes.data || []);
    } catch (err) {
      console.log(err);
      setError("Failed to load blog posts.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadPosts();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [statusFilter, categoryFilter]);

  const filteredPosts = useMemo(() => {
    const query = search.trim().toLowerCase();
    if (!query) return posts;
    return posts.filter((post) => {
      const haystack = [
        post.title,
        post.excerpt,
        post.slug,
        ...(post.tags || []),
        ...(post.categories || []).map((c) => c.name),
      ]
        .filter(Boolean)
        .join(" ")
        .toLowerCase();
      return haystack.includes(query);
    });
  }, [posts, search]);

  const stats = useMemo(() => {
    const published = posts.filter((p) => p.status === "published").length;
    return {
      total: posts.length,
      published,
      draft: posts.length - published,
      categories: categories.length,
    };
  }, [posts, categories]);

  const handleDelete = async () => {
    if (!deleteTarget?._id) return;
    setDeleteLoading(true);
    setDeleteError("");
    try {
      await instance.delete(DELETE_BLOG(deleteTarget._id));
      setDeleteTarget(null);
      await loadPosts();
    } catch (err) {
      console.log(err);
      setDeleteError("Failed to delete post.");
    } finally {
      setDeleteLoading(false);
    }
  };

  const handleAddCategory = async () => {
    const name = categoryName.trim();
    if (!name) {
      setCategoryError("Category name is required.");
      return;
    }
    setCategoryLoading(true);
    setCategoryError("");
    try {
      await instance.post(BLOG_CATEGORIES, { name });
      setCategoryName("");
      const categoriesRes = await instance.get(BLOG_CATEGORIES);
      setCategories(categoriesRes.data || []);
    } catch (err) {
      console.log(err);
      setCategoryError("Failed to create category.");
    } finally {
      setCategoryLoading(false);
    }
  };

  const handleDeleteCategory = async (id) => {
    try {
      await instance.delete(DELETE_BLOG_CATEGORY(id));
      const categoriesRes = await instance.get(BLOG_CATEGORIES);
      setCategories(categoriesRes.data || []);
      if (categoryFilter === id) {
        setCategoryFilter("all");
      }
      await loadPosts();
    } catch (err) {
      console.log(err);
      setCategoryError("Failed to delete category.");
    }
  };

  return (
    <div className="vehicles-page">
      <Breadcrumbs aria-label="breadcrumb" className="mb-3">
        <Link to="/">Home</Link>
        <Typography>Blogs</Typography>
      </Breadcrumbs>

      <section className="vehicles-hero">
        <div>
          <Typography className="vehicles-hero-eyebrow">
            <ArticleOutlinedIcon fontSize="inherit" /> Content
          </Typography>
          <Typography variant="h4" className="vehicles-hero-title">
            Blogs
          </Typography>
          <Typography className="vehicles-hero-subtitle">
            Create and publish articles with categories, tags, and rich content.
          </Typography>
        </div>
        <div className="d-flex gap-2 flex-wrap">
          <Button
            variant="outlined"
            startIcon={<CategoryOutlinedIcon />}
            onClick={() => setShowCategories(true)}
            sx={{ color: "#fff", borderColor: "rgba(255,255,255,0.45)" }}
          >
            Categories
          </Button>
          <Button
            component={Link}
            to="/blogs/new"
            variant="contained"
            startIcon={<AddIcon />}
          >
            Add Post
          </Button>
        </div>
      </section>

      <div className="vehicles-stats-grid mt-3">
        <div className="vehicles-stat-card">
          <Typography className="vehicles-stat-value">{stats.total}</Typography>
          <Typography className="vehicles-stat-label">Total posts</Typography>
        </div>
        <div className="vehicles-stat-card">
          <Typography className="vehicles-stat-value">{stats.published}</Typography>
          <Typography className="vehicles-stat-label">Published</Typography>
        </div>
        <div className="vehicles-stat-card">
          <Typography className="vehicles-stat-value">{stats.draft}</Typography>
          <Typography className="vehicles-stat-label">Drafts</Typography>
        </div>
        <div className="vehicles-stat-card">
          <Typography className="vehicles-stat-value">{stats.categories}</Typography>
          <Typography className="vehicles-stat-label">Categories</Typography>
        </div>
      </div>

      {error ? (
        <Alert severity="error" className="mt-3">
          {error}
        </Alert>
      ) : null}

      <section className="vehicles-toolbar mt-3">
        <div className="vehicles-toolbar-filters">
          <Form.Control
            className="admin-search"
            placeholder="Search posts..."
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          <Form.Select
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
          >
            <option value="all">All statuses</option>
            <option value="published">Published</option>
            <option value="draft">Draft</option>
          </Form.Select>
          <Form.Select
            value={categoryFilter}
            onChange={(e) => setCategoryFilter(e.target.value)}
          >
            <option value="all">All categories</option>
            {categories.map((cat) => (
              <option key={cat._id} value={cat._id}>
                {cat.name}
              </option>
            ))}
          </Form.Select>
        </div>
      </section>

      <section className="section mt-3">
        {loading ? (
          <div className="d-flex justify-content-center py-5">
            <CircularProgress size={32} />
          </div>
        ) : filteredPosts.length === 0 ? (
          <div className="text-center py-5">
            <ArticleOutlinedIcon sx={{ fontSize: 48, color: "text.disabled" }} />
            <Typography color="text.secondary" className="mt-2">
              No blog posts found.
            </Typography>
            <Button
              component={Link}
              to="/blogs/new"
              variant="contained"
              className="mt-3"
              startIcon={<AddIcon />}
            >
              Create your first post
            </Button>
          </div>
        ) : (
          <div className="blogs-grid">
            {filteredPosts.map((post) => (
              <article key={post._id} className="blog-card">
                <div className="blog-card-media">
                  {post.featuredImage ? (
                    <img src={resolveImageUrl(post.featuredImage)} alt="" />
                  ) : (
                    <div className="blog-card-media-fallback">
                      <ArticleOutlinedIcon />
                    </div>
                  )}
                  <Chip
                    size="small"
                    label={post.status === "published" ? "Published" : "Draft"}
                    color={post.status === "published" ? "success" : "default"}
                    className="blog-card-status"
                  />
                </div>
                <div className="blog-card-body">
                  <Typography className="blog-card-title">{post.title}</Typography>
                  <Typography className="blog-card-excerpt">
                    {post.excerpt || "No excerpt"}
                  </Typography>
                  <div className="blog-card-meta">
                    <span>{formatDate(post.publishedAt || post.updatedAt)}</span>
                    {(post.categories || []).slice(0, 2).map((cat) => (
                      <Chip key={cat._id || cat} size="small" label={cat.name || cat} />
                    ))}
                  </div>
                </div>
                <div className="blog-card-actions">
                  <Tooltip title="Edit">
                    <IconButton
                      size="small"
                      component={Link}
                      to={`/blogs/${post._id}`}
                    >
                      <EditOutlinedIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                  <Tooltip title="Delete">
                    <IconButton
                      size="small"
                      color="error"
                      onClick={() => {
                        setDeleteError("");
                        setDeleteTarget(post);
                      }}
                    >
                      <DeleteOutlineIcon fontSize="small" />
                    </IconButton>
                  </Tooltip>
                </div>
              </article>
            ))}
          </div>
        )}
      </section>

      <Dialog
        open={Boolean(deleteTarget)}
        onClose={() => !deleteLoading && setDeleteTarget(null)}
        maxWidth="xs"
        fullWidth
      >
        <DialogTitle>Delete post?</DialogTitle>
        <DialogContent>
          {deleteError ? (
            <Alert severity="error" sx={{ mb: 2 }}>
              {deleteError}
            </Alert>
          ) : null}
          <DialogContentText>
            Permanently delete <strong>{deleteTarget?.title}</strong>? This cannot
            be undone.
          </DialogContentText>
        </DialogContent>
        <DialogActions>
          <Button
            onClick={() => setDeleteTarget(null)}
            disabled={deleteLoading}
          >
            Cancel
          </Button>
          <LoadingButton
            color="error"
            variant="contained"
            loading={deleteLoading}
            onClick={handleDelete}
          >
            Delete
          </LoadingButton>
        </DialogActions>
      </Dialog>

      <Modal
        show={showCategories}
        onHide={() => !categoryLoading && setShowCategories(false)}
        centered
      >
        <Modal.Header closeButton>
          <Modal.Title>Manage categories</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {categoryError ? (
            <Alert severity="error" className="mb-3">
              {categoryError}
            </Alert>
          ) : null}
          <div className="d-flex gap-2 mb-3">
            <Form.Control
              placeholder="New category name"
              value={categoryName}
              onChange={(e) => setCategoryName(e.target.value)}
            />
            <LoadingButton
              variant="contained"
              loading={categoryLoading}
              onClick={handleAddCategory}
            >
              Add
            </LoadingButton>
          </div>
          {categories.length === 0 ? (
            <Typography color="text.secondary">No categories yet.</Typography>
          ) : (
            <ul className="blog-category-list">
              {categories.map((cat) => (
                <li key={cat._id}>
                  <span>{cat.name}</span>
                  <IconButton
                    size="small"
                    color="error"
                    onClick={() => handleDeleteCategory(cat._id)}
                  >
                    <DeleteOutlineIcon fontSize="small" />
                  </IconButton>
                </li>
              ))}
            </ul>
          )}
        </Modal.Body>
      </Modal>
    </div>
  );
}

export default Blogs;
